import type {
  DateSelectArg,
  DatesSetArg,
  EventApi,
  EventClickArg,
  EventContentArg,
  SlotLaneContentArg,
} from '@fullcalendar/core';
import type { EventResizeStopArg } from '@fullcalendar/interaction';

import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import type { Theme } from '@mui/material/styles';
import { Box } from '@src/components';
import { useAuthentication } from '@src/context/hooks';
import { toTime } from '@src/utils';
import { useCallback, useMemo, useRef, useState } from 'react';
import { ABS_DAY_VIEW, DB_CLICK_THRESHHOLD } from '../../constants';
import useAbsencesTimeline from '../../hooks';
import {
  absenceSelectionToSlot,
  absencesToEvents,
  getAbsenceSelectionById,
  getSlotDatefromSelection,
  getTransientEventsForSlot,
  usersToCalendarResources,
} from '../../utils';
import { Header } from './components';

type LastDates = null | { start: number; end: number };
type LastClick = { id: number | null; time: number };

export default function Timeline() {
  const lastDatesRef = useRef<LastDates>(null);
  const lastClickRef = useRef<LastClick>({ id: null, time: 0 });

  const [currentDates, setCurrentDates] = useState({
    start: new Date(),
    end: new Date(),
  });

  const { user } = useAuthentication();
  const {
    absences,
    users,
    view,
    setView,
    selectedSlot,
    setSelectedSlot,
    attachCalendar,
    calendar,
    isMonthOrHalfMonthView,
    isDayView,
  } = useAbsencesTimeline();

  const timelineResources = useMemo(() => {
    const resources = usersToCalendarResources(users, user);
    return resources;
  }, [user, users]);

  const timelineEvents = useMemo(() => {
    return [
      ...absencesToEvents(absences, isDayView),
      ...getTransientEventsForSlot(selectedSlot, absences, users, isDayView),
    ];
  }, [users, absences, selectedSlot, isDayView]);

  const onSelect = (selectionInfo: DateSelectArg) => {
    const slotData = getSlotDatefromSelection(selectionInfo, absences);
    setSelectedSlot(slotData);
  };

  const onAbsenceClick = useCallback(
    (info: EventClickArg) => {
      const now = Date.now();
      const id = Number(info.event.id);
      const { id: prevId, time: prevTime } = lastClickRef.current;
      const isSame = prevId === id;
      const isDbl = isSame && now - prevTime < DB_CLICK_THRESHHOLD;

      const absenceId = Number(info.event.id);

      let selection = getAbsenceSelectionById(absenceId, absences, true);
      if (isDbl) {
        selection = getAbsenceSelectionById(absenceId, absences, isDayView);
      }
      if (selection) {
        setSelectedSlot(absenceSelectionToSlot(selection));
      }

      lastClickRef.current = { id, time: now };
    },
    [absences, setSelectedSlot, isDayView],
  );

  const getAbsenceBaseClasses = (event: EventApi) => {
    const pos = event.extendedProps.block_position;
    const classes = ['abs-base'];
    if (pos && isMonthOrHalfMonthView) {
      classes.push(`abs-${pos}`);
    }
    if (!event.allDay && isMonthOrHalfMonthView) {
      classes.push('abs-partial');
    }
    return classes;
  };

  const getAbsenceSelectedClasses = (event: EventApi) => {
    if (selectedSlot) {
      const selectedIds = selectedSlot.selectedIds;
      if (selectedIds.includes(Number(event.id))) {
        return ['abs-selected'];
      }
    }
    return [];
  };

  const eventClassNames = (arg: { event: EventApi }) => {
    const event: EventApi = arg.event;
    const base_absence_cls = getAbsenceBaseClasses(event);
    const selected_absence_cls = getAbsenceSelectedClasses(event);
    return [...base_absence_cls, ...selected_absence_cls];
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    const newStart = arg.start.getTime();
    const newEnd = arg.end.getTime();

    const { start: lastStart, end: lastEnd } = lastDatesRef.current || {};
    if (newStart !== lastStart && newEnd !== lastEnd) {
      const newDates = { start: arg.start.getTime(), end: arg.end.getTime() };
      lastDatesRef.current = newDates;
      setCurrentDates({ start: arg.start, end: arg.end });
    }
    if (view?.type !== arg.view.type) {
      setView(arg.view);
    }
  };

  const onSlotLaneClassNames = (arg: SlotLaneContentArg) => {
    if (!arg.date) {
      return [];
    }
    const day = arg.date.getDay();
    const isWeekend = day === 0 || day === 6;

    const today = new Date();
    const isToday =
      arg.date.getFullYear() === today.getFullYear() &&
      arg.date.getMonth() === today.getMonth() &&
      arg.date.getDate() === today.getDate();

    return [isWeekend && 'fc-weekend-slot', isToday && 'fc-today-slot'].filter(
      (cls): cls is string => Boolean(cls),
    );
  };

  const onNavLinkDay = useCallback(
    (date: Date) => {
      calendar.changeView(ABS_DAY_VIEW, date);
    },

    [calendar],
  );

  const setCalendarRef = useCallback(
    (node: FullCalendar) => {
      attachCalendar(node);
    },
    [attachCalendar],
  );

  const onEventContent = (args: EventContentArg) => {
    const label = args.event.extendedProps.label;
    if (label) {
      return {
        html: `<div class="abs-event-content">${label}</div>`,
      };
    }
    const show_workday_count = args.event.extendedProps.show_workday_count;
    if (show_workday_count) {
      const count = args.event.extendedProps.workday_count;
      return { html: `<div class="abs-workday-count">${count}</div>` };
    }
    return '';
  };
  const onEventResize = (info: EventResizeStopArg) => {
    const start = info.event.start;
    const end = info.event.end;
    setSelectedSlot((slot) => {
      if (!slot) return null;
      return {
        ...slot,
        startTime: start ? toTime(start) : null,
        endTime: end ? toTime(end) : null,
      };
    });
  };

  return (
    <Box sx={StyleSheet.calendar}>
      <Header currentDates={currentDates} />

      <FullCalendar
        ref={setCalendarRef}
        height="900px"
        navLinks={true}
        select={onSelect}
        events={timelineEvents}
        datesSet={handleDatesSet}
        eventClick={onAbsenceClick}
        eventResize={onEventResize}
        navLinkDayClick={onNavLinkDay}
        eventClassNames={eventClassNames}
        initialView="resourceTimelineMonth"
        plugins={[resourceTimelinePlugin, interactionPlugin]}
        views={{
          resourceTimelineMonth: {
            selectable: true,
            dayHeaderFormat: { month: 'numeric', day: 'numeric' },
            slotLabelFormat: [{ day: 'numeric' }],
          },
          resourceTimelineHalfMonth: {
            type: 'resourceTimeline',
            selectable: true,
            slotDuration: { days: 1 },
            slotLabelFormat: [{ day: 'numeric' }],
          },
          resourceTimelineDay: {
            selectable: true,
            slotLabelFormat: [{ hour: 'numeric', hour12: false }],
            slotMinWidth: 20,
          },
        }}
        resourceAreaWidth={350}
        resourceOrder="order,title"
        resources={timelineResources}
        headerToolbar={{ left: '', right: '' }}
        slotLaneClassNames={onSlotLaneClassNames}
        eventContent={onEventContent}
        resourceAreaHeaderContent=""
      />
    </Box>
  );
}

const StyleSheet = {
  calendar: (theme: Theme) => ({
    flex: 1,
    width: '100%',
    padding: '70px 0 0 30px',
    '.fc-header-toolbar': {
      '.fc-toolbar-chunk > div': {
        display: 'flex',
        h2: { margin: '0 10px' },
      },
    },
    '.fc .fc-timeline-header-row-chrono .fc-timeline-slot-frame': {
      justifyContent: 'center',
    },
    '.fc-weekend-slot': {
      background: 'rgba(0, 0, 0, 0.05)',
    },

    '.fc-today-slot': {
      background: theme.palette.blue[120],
    },

    '.fc-timeline-slot.fc-weekend-slot': {
      background: 'rgba(0, 0, 0, 0.08)',
    },
    '.abs-base': {
      height: '28px',
      padding: '0 6px',
    },
    '.abs-start': { borderRadius: '100px 0 0 100px' },
    '.abs-middle': { borderRadius: 0 },
    '.abs-end': { borderRadius: '0 100px 100px 0' },
    '.abs-single': { borderRadius: '100px' },
    '.abs-selected': {
      border: '3px dashed !important',
      borderColor: theme.palette.common.black,
    },
    '.abs-workday-count': {
      color: theme.palette.common.white,
      fontWeight: 'bold',
      fontSize: '14px',
      textAlign: 'center',
      lineHeight: '28px',
      width: '100%',
    },
    '.abs-partial': {
      background: 'transparent !important',
      border: '3px solid',
    },
    '.abs-selected.abs-partial': {
      background: `${theme.palette.grey[200]} !important`,
    },
    '.fc-event.transient-event': {
      opacity: 0.6,
      border: '1px solid',
      borderColor: theme.palette.common.black,
      background: theme.palette.grey[500],
    },
    '.abs-event-content': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 900,
      color: theme.palette.common.white,
    },
    '.fc-event.transient-event .abs-event-content': {
      color: theme.palette.common.black,
    },
  }),
};
